import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, MessageSquare, Wrench } from 'lucide-react';
import { COLORS } from '../constants';
import Button from '../components/Button';
import SectionHeading from '../components/SectionHeading';

const SupportPage = () => {
  const [formState, setFormState] = useState('idle');
  const handleSubmit = (e) => { e.preventDefault(); setFormState('sending'); setTimeout(() => setFormState('success'), 1500); };

  if (formState === 'success') {
    return (
      <section className="relative py-20 min-h-screen flex items-center justify-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="a+6.jpg" loading="lazy"  decoding="async" alt="Background" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: `${COLORS.lightSurface}E6` }} />
        </div>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative z-10 bg-white p-16 rounded-[3rem] shadow-2xl text-center max-w-md w-full border-t-[12px]" style={{ borderTopColor: COLORS.accent }}>
          <CheckCircle size={56} className="mx-auto mb-8" style={{ color: COLORS.accent }} />
          <h2 className="text-4xl font-serif font-bold mb-6">Request Logged</h2>
          <Button variant="secondary" className="w-full" onClick={() => setFormState('idle')}>Back to Support</Button>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="relative py-20 min-h-screen overflow-hidden">
	<Helmet>
  <title>Electric Golf Scooter Support & Service | Lectric Leisure</title>
  <link rel="canonical" href="https://lectricleisure.golf/support" />
  <meta
    name="description"
    content="Get expert support, service, and technical assistance for your Lectric Leisure electric golf scooter."
  />
</Helmet>
      <div className="absolute inset-0 z-0">
        <img src="a+6.jpg" loading="lazy"  decoding="async" alt="Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: `${COLORS.lightSurface}F2` }} />
      </div>
      <div className="relative z-10">
        <SectionHeading title="Technical Service" subtitle="Expert help for your Lectric scooter." />
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row border border-[#E0D8C8]">
            <div className="lg:w-1/3 p-12 text-[#F2EBDC]" style={{ backgroundColor: COLORS.secondaryBg }}>
               <h3 className="text-3xl font-serif font-bold mb-12">Service Perks</h3>
               <div className="space-y-10">
		 <div className="flex gap-6"><Clock size={28} color={COLORS.accent} /><p className="text-lg">Fast Response Time</p></div>
                 <div className="flex gap-6"><MessageSquare size={28} color={COLORS.accent} /><p className="text-lg">Expert Consultation</p></div>
                 <div className="flex gap-6"><Wrench size={28} color={COLORS.accent} /><p className="text-lg">Original Parts</p></div>
               </div>
            </div>
            <div className="lg:w-2/3 p-12 relative overflow-hidden">
              <div className="absolute inset-0 z-0"><img src="a+4.jpg" loading="lazy"  decoding="async" className="w-full h-full object-cover blur-sm opacity-20" alt="" /></div>
              <form
		  onSubmit={async (e) => {
		    e.preventDefault();
		    setFormState("sending");

		    try {
		      const res = await fetch("https://formspree.io/f/mlgdqwvn", {
		        method: "POST",
		        headers: { "Accept": "application/json" },
		        body: new FormData(e.target),
      });

      if (res.ok) setFormState("success");
      else setFormState("idle");
    } catch {
      setFormState("idle");
    }
  }}
  className="space-y-8 relative z-10"
>
  		<input type="hidden" name="form-name" value="support" />
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-extrabold text-[#1E1E1C] mb-3 uppercase tracking-widest">Select Model</label>
                    <select name="model" className="w-full p-5 rounded-2xl bg-[#F9F7F2]/90 border-2 border-[#E0D8C8] focus:border-[#C7A36A] outline-none transition-colors shadow-inner font-bold backdrop-blur-sm text-[#1E1E1C]">
  			<option value="">Select Model...</option>
			<option>M12-1</option>
  			<option>M12-3</option>
  			<option>ESGOLF</option>
  		          </select>
                  </div>
                  <div>
                    <label className="block text-sm font-extrabold text-[#1E1E1C] mb-3 uppercase tracking-widest">Customer Issue</label>
                    <select  name="issue"  className="w-full p-5 rounded-2xl bg-[#F9F7F2]/90 border-2 border-[#E0D8C8] focus:border-[#C7A36A] outline-none transition-colors shadow-inner font-bold backdrop-blur-sm text-[#1E1E1C]" >
			  <option>Battery Issues</option>
			  <option>Motor Issues</option>
			  <option>Chassis & Steering</option>
			  <option>Upgrades</option>
			  <option>Service Manual</option>
		   </select>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-extrabold text-[#1E1E1C] mb-3 uppercase tracking-widest">Full Name</label>
                    <input required name="fullName" className="w-full p-5 rounded-2xl bg-[#F9F7F2]/90 border-2 border-[#E0D8C8] text-[#1E1E1C] outline-none font-bold" />
                  </div>
                  <div>
                    <label className="block text-sm font-extrabold text-[#1E1E1C] mb-3 uppercase tracking-widest">Email</label>
                    <input required type="email" name="email" className="w-full p-5 rounded-2xl bg-[#F9F7F2]/90 border-2 border-[#E0D8C8] text-[#1E1E1C] outline-none font-bold" />
                  </div>
                </div>

                <textarea required rows="4" name="description" placeholder="Issue Description" className="w-full p-5 rounded-2xl bg-[#F9F7F2]/90 border-2 border-[#E0D8C8] outline-none font-bold text-[#1E1E1C]"></textarea>
                <Button type="submit" disabled={formState === 'sending'} className="w-full py-6 text-2xl">{formState === 'sending' ? 'Sending...' : 'Send Ticket'}</Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportPage;